/**
 * Renders a follow button that enables management for board followers
 * The follow button options change based on the "owner" of the button and board.
 * The renderable action of the button(s) has the following rules:
 *  1. If the current user is the owner or collaborator of the board, then don't show anything
 *  2. Otherwise, if the user is a follower of the board, show a follow or unfollow state
 *
 * Copyright (c) 2019 Propellant Software Corp
 * Author: Kelvin Nguyen
 */

import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { forceAuthRedirect } from 'client/shared/js/services/auth/api'
import LoadButton from 'client/shared/js/components/LoadButton'
import FavoriteButton from 'client/shared/js/components/FavoriteButton'
import {
  isBoardOwner,
  isBoardCollaborator,
  isFollowingBoard
} from '../../../utils/accessInterpreter/board'

export default class BoardFollowButton extends Component {
  static propTypes = {
    user: PropTypes.object,
    board: PropTypes.object.isRequired,
    status: PropTypes.object,
    onFollowBoard: PropTypes.func,
    onUnfollowBoard: PropTypes.func,
    heart: PropTypes.bool,
    navigate: PropTypes.func
  }

  static defaultProps = {
    user: {},
    board: {},
    status: {},
    heart: false,
    onFollowBoard: () => false,
    onUnfollowBoard: () => false
  }

  isLoading = () => {
    const followRequest = this.props.status.followRequest
    return !!(followRequest && followRequest[this.props.board.bid])
  }

  handleFollowBoard = () => {
    this.props.onFollowBoard(this.props.board.bid)
  }

  handleUnfollowBoard = () => {
    this.props.onUnfollowBoard(this.props.board.bid)
  }

  renderUnfollowButton = (isAuthenticated) => {
    if (this.props.heart) {
      // If we are rendering the unfollow button, that means it has been favorited
      return this.renderHeartButton(true, isAuthenticated)
    }

    const btnProps = {
      isLoading: this.isLoading(),
      className: 'follow-btn',
      size: 'sm',
      color: 'light',
      onClick: this.handleUnfollowBoard
    }

    return (
      <LoadButton {...btnProps}>Favorited</LoadButton>
    )
  }

  renderFollowButton = (isAuthenticated) => {
    if (this.props.heart) {
      // If we are rendering the follow button, that means it has not been favorited
      return this.renderHeartButton(false, isAuthenticated)
    }

    const btnProps = {
      isLoading: this.isLoading(),
      className: 'follow-btn',
      size: 'sm',
      onClick: (isAuthenticated) ? this.handleFollowBoard : () => forceAuthRedirect(null, false, { navigate: this.props.navigate })
    }

    return (
      <LoadButton {...btnProps}>Favorite</LoadButton>
    )
  }

  renderHeartButton = (favorite = false, isAuthenticated) => {
    const btnProps = {
      id: `favorite-btn-${this.props.board.bid}`,
      favorite,
      favoriteTooltip: 'Click to remove this board from your favorites',
      unfavoriteTooltip: 'Click to add this board to your favorites',
      onFavorite: (isAuthenticated) ? this.handleFollowBoard : () => forceAuthRedirect(null, false, { navigate: this.props.navigate }),
      onUnfavorite: (isAuthenticated) ? this.handleUnfollowBoard : () => forceAuthRedirect(null, false, { navigate: this.props.navigate })
    }

    return (
      <FavoriteButton {...btnProps} />
    )
  }

  render () {
    const isOwner = isBoardOwner(this.props.user, this.props.board)
    const isCollaborator = isBoardCollaborator(this.props.user, this.props.board)
    const isFollowing = isFollowingBoard(this.props.user, this.props.board)

    if (!isOwner && !isCollaborator) {
      // If a user is signed in, show the appropriate follow buttons
      if (this.props.user.uid) {
        if (isFollowing) {
          return this.renderUnfollowButton(true)
        } else {
          return this.renderFollowButton(true)
        }
      } else {
        return this.renderFollowButton(false)
      }
    }

    return null
  }
}
